import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { type LanguageCode, languages } from '@/i18n';

export function useLanguage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ lang?: string }>();

  // Get current language from URL or fallback to i18n language
  const lang = (params.lang as LanguageCode) || (i18n.language as LanguageCode) || 'sv';

  // Sync i18n with URL language
  useEffect(() => {
    if (params.lang && params.lang !== i18n.language) {
      const validLang = languages.find((l) => l.code === params.lang);
      if (validLang) {
        i18n.changeLanguage(params.lang);
      }
    }
  }, [params.lang, i18n]);

  // Sync document lang attribute for a11y and SEO
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const changeLanguage = useCallback(
    (newLang: LanguageCode) => {
      i18n.changeLanguage(newLang);
      
      // Replace current language in path with new language
      const pathWithoutLang = location.pathname.replace(/^\/(sv|da|fi|no|en)/, '');
      const newPath = `/${newLang}${pathWithoutLang || ''}`;
      
      navigate(newPath + location.search + location.hash, { replace: true });
    },
    [i18n, location.pathname, navigate]
  );

  const getLocalizedPath = useCallback(
    (path: string) => {
      // Remove leading slash and any existing language prefix (must be followed by / or end of string)
      const cleanPath = path.replace(/^\/(sv|da|fi|no|en)(\/|$)/, '/').replace(/^\//, '');
      return `/${lang}/${cleanPath}`.replace(/\/$/, '') || `/${lang}`;
    },
    [lang]
  );

  return {
    lang,
    changeLanguage,
    getLocalizedPath,
    currentLanguage: languages.find((l) => l.code === lang) || languages[0],
  };
}